import React, { useState } from "react";
import { dbService } from "../../firebaseInstance";
import { useThreadsAuth } from "../../hooks/useThreadsAuth";
import BrandVideoTemplate from "../../template/brand/BrandVideoTemplate";
import LoginPresent from "./LoginPresent";
import { Box, Button, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import logoImage from "../../assets/logo/logo-full.svg";

/**
 * VideoLogin 컴포넌트
 * 비디오 스크러빙 브랜드 스토리와 로그인 기능을 통합한 새로운 로그인 경험
 */
const VideoLogin = () => {
  const { login: threadsLogin } = useThreadsAuth();
	const [showVideoExperience, setShowVideoExperience] = useState(true);
	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	// 비디오 완료 핸들러
	const handleVideoComplete = () => {
		console.log('🎬 Brand video experience completed');
		setShowVideoExperience(false);
	};

  const handleLogin = () => {
    threadsLogin();
  };

	const handleWaitlist = async () => {
		if (!name || !email) return;

		setIsSubmitting(true);
		try {
			const waitlistRef = dbService.collection("waitlist").doc(name);
			await waitlistRef.set({
				name,
				email,
				createdAt: new Date().toISOString(),
			});

			setName("");
			setEmail("");
			alert("Successfully joined waitlist!");
		} catch (error) {
			console.error("Error adding to waitlist:", error);
			alert("Failed to join waitlist. Please try again.");
		} finally {
			setIsSubmitting(false);
		}
	};

	// LoginTopSection 스타일을 적용한 모션 애니메이션
	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: 0.3,
				duration: 0.4,
				ease: "easeInOut"
			}
		}
	};
	
	const itemVariants = {
		hidden: { opacity: 0, y: 20, scale: 4 },
		visible: { 
			opacity: 1, 
			y: 0,
			scale: 1,
			transition: { 
				duration: 0.4,
				ease: "easeInOut"
			}
		}
	};

	// 비디오 경험을 보여줄지 결정
	if (showVideoExperience) {
		return (
			<Box sx={{ position: 'relative' }}>
				{/* 브랜드 로고 오버레이 - LoginTopSection 스타일 적용 */}
				<Box
					sx={{
						position: 'fixed',
						top: 40,
						left: '50%',
						transform: 'translateX(-50%)',
						zIndex: 1001,
						textAlign: 'center'
					}}
				>
					<motion.div
						variants={containerVariants}
						initial="hidden"
						animate="visible"
					>
						<motion.div variants={itemVariants}>
							<Box sx={{ mb: 1 }}>
								<img
									src={logoImage}
									alt="Logo"
									style={{
										height: '32px',
										width: 'auto',
										filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
									}}
								/>
							</Box>
						</motion.div>
						
						<motion.div variants={itemVariants}>
							<Typography
								sx={{
									fontWeight: 200,
									fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.8rem' },
									color: '#333333',
									textShadow: '0 1px 2px rgba(255,255,255,0.8)',
									letterSpacing: '0.02em'
								}}
							>
								브랜드 스토리 체험
							</Typography>
						</motion.div>
					</motion.div>
				</Box>

				{/* 스킵 버튼 - LoginTopSection 타이포그래피 스타일 적용 */}
				<Box
					sx={{
						position: 'fixed',
						top: 20,
						right: 20,
						zIndex: 1000
					}}
				>
					<Button
						variant="outlined"
						size="small"
						onClick={() => setShowVideoExperience(false)}
						sx={{ 
							backgroundColor: 'rgba(0, 0, 0, 0.5)',
							color: '#FFFFFF',
							border: '1px solid rgba(255, 255, 255, 0.3)',
							fontWeight: 200,
							fontSize: '0.875rem',
							letterSpacing: '0.02em',
							'&:hover': {
								backgroundColor: 'rgba(0, 0, 0, 0.7)',
								border: '1px solid rgba(255, 255, 255, 0.5)',
							}
						}}
					>
						건너뛰기
					</Button>
				</Box>

				<BrandVideoTemplate 
					onVideoComplete={handleVideoComplete}
				/>
			</Box>
		);
	}

	// 로그인 화면
	return (
		<Box sx={{ position: 'relative', width: '100%', height: '100vh' }}>
			{/* 브랜드 스토리 다시 보기 버튼 - LoginTopSection 타이포그래피 스타일 적용 */}
			<Box
				sx={{
					position: 'absolute',
					top: 20,
					right: 20,
					zIndex: 10
				}}
			>
				<Button
					variant="outlined"
					size="small"
					onClick={() => setShowVideoExperience(true)}
					sx={{
						backgroundColor: "rgba(0, 0, 0, 0.05)",
						color: "#000",
						border: "1px solid rgba(0, 0, 0, 0.2)",
						fontWeight: 200,
						fontSize: '0.875rem',
						letterSpacing: '0.02em',
						"&:hover": {
							backgroundColor: "rgba(0, 0, 0, 0.1)",
							border: "1px solid rgba(0, 0, 0, 0.3)",
						},
					}}
				>
					브랜드 스토리 다시 보기
				</Button>
			</Box>

			{/* 로그인 컴포넌트 */}
			<LoginPresent
				name={name}
				email={email}
				isSubmitting={isSubmitting}
				handleLogin={handleLogin}
				handleWaitlist={handleWaitlist}
				setName={setName}
				setEmail={setEmail}
			/>
		</Box>
	);
};

export default VideoLogin; 